import { Suspense } from "react";
import { lazyRetry as lazy } from "@/lib/lazy-retry";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { CompanyModeProvider } from "@/hooks/useCompanyMode";
import { VerticalProvider } from "@/hooks/useVertical";
import { GlobalErrorProvider } from "@/components/GlobalErrorProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import ScrollToTop from "@/components/ScrollToTop";
import { CinematicTransitionProvider } from "@/components/landing/CinematicTransitionProvider";
import { CinematicTransitionLayer } from "@/components/landing/CinematicTransitionLayer";
import { DashRouteTracer } from "@/components/dash-v2/DashRouteTracer";
import { RouteProgressBar } from "@/components/dash-v2/RouteProgressBar";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PhaseGate } from "@/components/PhaseGate";
import { AuditModeRedirect } from "@/components/AuditModeRedirect";
import { AdminRouteBoundary } from "@/components/admin/AdminRouteBoundary";
import { registryFeatures } from "@/lib/feature-flags";
import { QuebecLanguageBanner } from "@/components/QuebecLanguageBanner";

import {
  // Eager
  Index, Login, Signup, SignupRolePicker, NotFound, AuthCallback,
  // Homepage v2 preview (temporary · /preview-home)
  HomeV2,
  // Auth
  MfaVerify, ForgotPassword, ResetPassword, VerifyEmail,
  // Public
  DocsPage, AboutPage, PricingPage, SwitchingFromCarrier411Page, CoveragePage, AgentSignupPage, ConnectPage,
  ClaimAgentPage, CarrierOnboarding, StateRegistryPage,
  ScaleDemoPage, DemoVideoPage, DashboardDemoPage, IntegrationGuidePage, QuickstartPage, ProductPage,
  BlogIndex, BlogArticle, BookDemoPage, PublicCarrierLookup,
  // CR Registry · Wave 2.7 (home) + Wave 2.8 (detail/SEO)
  RegistryHomePage, RegistryDetailPage, RegistrySeoPage,
  UnsubscribePage,
  // Public trust link + carrier profile
  TrustLinkDriverPage, CarrierProfilePage, BrokerProfilePage, ClaimVerifyPage, TrustLinkReceiptPage,
  // Legal
  SecurityPage, TermsPage, PrivacyPage, ApiTermsPage, CookiePolicyPage,
  // Trust
  StatusPage, ChangelogPage,
  // Mobile
  MobileLayout, MobileLoadFeed, MobileProfile, MobileAlerts,
} from "@/routes";

// Dash v2 rebuild: every legacy dashboard route collapses to this page until
// the new dash lands. See src/pages/UnderConstructionPage.tsx for context.
import UnderConstructionPage from "@/pages/UnderConstructionPage";

// Dash v2 (in progress) · mounts under /dash during the rebuild window.
// All dash pages are lazy-loaded so landing-page visitors don't pay for
// dashboard JS on first load. This dramatically improves mobile FCP and
// frees the main thread to respond to touch scrolling immediately.
const DashV2Layout = lazy(() => import("@/layouts/DashV2Layout"));
const SystemStatePage = lazy(() => import("@/pages/dash-v2/SystemStatePage"));
const DispatcherHomePage = lazy(() => import("@/pages/dash-v2/DispatcherHomePage"));
const DispatcherHomeGate = lazy(() => import("@/pages/dash-v2/DispatcherHomeGate"));
const DispatcherOnboardingPage = lazy(() => import("@/pages/dash-v2/DispatcherOnboardingPage"));
const CarrierLookupPage = lazy(() => import("@/pages/dash-v2/CarrierLookupPage"));
const VerificationDashboardPage = lazy(() => import("@/pages/dash-v2/VerificationDashboardPage"));
const TrustLinksPage = lazy(() => import("@/pages/dash-v2/TrustLinksPage"));
const CarrierSignupPage = lazy(() => import("@/pages/dash-v2/CarrierSignupPage"));
const BrokerSignupPage = lazy(() => import("@/pages/dash-v2/BrokerSignupPage"));
const BrokerOnboardingPage = lazy(() => import("@/pages/dash-v2/BrokerOnboardingPage"));
const CarrierOnboardingPage = lazy(() => import("@/pages/dash-v2/CarrierOnboardingPage"));
const HomeGate = lazy(() => import("@/pages/dash-v2/HomeGate"));
const CarrierInviteLandingPage = lazy(() => import("@/pages/dash-v2/CarrierInviteLandingPage"));
const FollowUpsPage = lazy(() => import("@/pages/dash-v2/FollowUpsPage"));
const TrustLinkDriverPageV2 = lazy(() => import("@/pages/dash-v2/TrustLinkDriverPage"));
const TrustLinkPreviewPage = lazy(() => import("@/pages/dash-v2/TrustLinkPreviewPage"));
const CarrierInvitePreviewPage = lazy(() => import("@/pages/dash-v2/CarrierInvitePreviewPage"));
const DocumentsPage = lazy(() => import("@/pages/dash-v2/DocumentsPage"));
const SandboxSettingsPage = lazy(() => import("@/pages/dash-v2/SandboxSettingsPage"));
const BillingPageV2 = lazy(() => import("@/pages/dash-v2/BillingPage"));
const ReferralsPage = lazy(() => import("@/pages/dash-v2/ReferralsPage"));
const FleetTrackingPage = lazy(() => import("@/pages/dash-v2/FleetTrackingPage"));
const DispatcherLoadBoardPage = lazy(() => import("@/pages/dash-v2/DispatcherLoadBoardPage"));
const TeamPageV2 = lazy(() => import("@/pages/dash-v2/TeamPage"));
const AdminHubPageV2 = lazy(() => import("@/pages/dash-v2/AdminHubPage"));
const AdminRoadmapPage = lazy(() => import("@/pages/dash-v2/AdminRoadmapPage"));
const QACommandCenterPage = lazy(() => import("@/pages/dash-v2/admin/QACommandCenterPage"));
const AdminReferralsPage = lazy(() => import("@/pages/dash-v2/admin/AdminReferralsPage"));
const AdminTenantControlsPage = lazy(() => import("@/pages/dash-v2/admin/AdminTenantControlsPage"));
const AdminEmergencyControlsPage = lazy(() => import("@/pages/dash-v2/admin/AdminEmergencyControlsPage"));
const IntakeMonitorPage = lazy(() => import("@/pages/dash-v2/admin/IntakeMonitorPage"));
const PipelinesPage = lazy(() => import("@/pages/dash-v2/admin/PipelinesPage"));
const EmbedBadgeTesterPage = lazy(() => import("@/pages/dash-v2/admin/EmbedBadgeTesterPage"));
const DocReviewQueuePage = lazy(() => import("@/pages/dash-v2/admin/DocReviewQueuePage"));
const CABrokerClaimsPage = lazy(() => import("@/pages/dash-v2/admin/CABrokerClaimsPage"));
const DocBundlePage = lazy(() => import("@/pages/dash-v2/admin/DocBundlePage"));
const AdminBrokerPackageOpsPage = lazy(() => import("@/pages/dash-v2/admin/AdminBrokerPackageOpsPage"));
const AdminIngestHealthPage = lazy(() => import("@/pages/dash-v2/admin/AdminIngestHealthPage"));
const AdminAnalyticsPage = lazy(() => import("@/pages/dash-v2/admin/AdminAnalyticsPage"));
const AdminTractionPage = lazy(() => import("@/pages/dash-v2/admin/AdminTractionPage"));
const QACockpitPage = lazy(() => import("@/pages/dash-v2/admin/qa-cockpit/QACockpitPage"));
const CarrierDetailPage = lazy(() => import("@/pages/dash-v2/CarrierDetailPage"));
const MyTrustScorePage = lazy(() => import("@/pages/dash-v2/MyTrustScorePage"));
const BrokerLookupPage = lazy(() => import("@/pages/dash-v2/BrokerLookupPage"));
const BrokerDetailPage = lazy(() => import("@/pages/dash-v2/BrokerDetailPage"));
const CABrokerDetailPage = lazy(() => import("@/pages/dash-v2/CABrokerDetailPage"));
const CABrokerListPage = lazy(() => import("@/pages/dash-v2/CABrokerListPage"));
const CABrokerDocumentsPage = lazy(() => import("@/pages/dash-v2/CABrokerDocumentsPage"));
const CABrokerReferPage = lazy(() => import("@/pages/refer/CABrokerReferPage"));
const PackageInboxPage = lazy(() => import("@/pages/dash-v2/PackageInboxPage"));
const PublicPackageView = lazy(() => import("@/pages/PublicPackageView"));
const BrokerPackagePage = lazy(() => import("@/pages/dash-v2/BrokerPackagePage"));
const MyPartnersPage = lazy(() => import("@/pages/dash-v2/MyPartnersPage"));
const PartnerAlertsPage = lazy(() => import("@/pages/dash-v2/PartnerAlertsPage"));
const DevelopersPage = lazy(() => import("@/pages/dash-v2/DevelopersPage"));
const DeveloperMcpPage = lazy(() => import("@/pages/dash-v2/DeveloperPage"));
const DevelopersMarketingPage = lazy(() => import("@/pages/Developers"));
const BulkVerifyPage = lazy(() => import("@/pages/dash-v2/BulkVerifyPage"));
const SettingsHubPageV2 = lazy(() => import("@/pages/dash-v2/SettingsHubPage"));
const NotificationSettingsPage = lazy(() => import("@/pages/dash-v2/NotificationSettingsPage"));
const FeedbackPage = lazy(() => import("@/pages/dash-v2/FeedbackPage"));
const EmailSettingsPage = lazy(() => import("@/pages/dash-v2/EmailSettingsPage"));
const VerificationSettingsPage = lazy(() => import("@/pages/dash-v2/VerificationSettingsPage"));
const LocaleSettingsPage = lazy(() => import("@/pages/dash-v2/LocaleSettingsPage"));
const DocumentSettingsPage = lazy(() => import("@/pages/dash-v2/DocumentSettingsPage"));
const DealSettingsPage = lazy(() => import("@/pages/dash-v2/DealSettingsPage"));
const TrustSettingsPage = lazy(() => import("@/pages/dash-v2/TrustSettingsPage"));
const SecuritySettingsPage = lazy(() => import("@/pages/dash-v2/SecuritySettingsPage"));
const RateSettingsPage = lazy(() => import("@/pages/dash-v2/RateSettingsPage"));
const IntegrationSettingsPage = lazy(() => import("@/pages/dash-v2/IntegrationSettingsPage"));
const EmailSequencesPage = lazy(() => import("@/pages/dash-v2/EmailSequencesPage"));
const EmailTemplatesPage = lazy(() => import("@/pages/dash-v2/EmailTemplatesPage"));
const EmailAIPage = lazy(() => import("@/pages/dash-v2/EmailAIPage"));
const EmailScannerPage = lazy(() => import("@/pages/dash-v2/email/tools/ScannerPage"));
const EmailSchedulePage = lazy(() => import("@/pages/dash-v2/email/tools/SchedulePage"));
const SentPage = lazy(() => import("@/pages/dash-v2/email/SentPage"));
const KnowledgeBasePage = lazy(() => import("@/pages/dash-v2/email/KnowledgeBasePage"));
const EmailPlaybooksPage = lazy(() => import("@/pages/dash-v2/email/tools/PlaybooksPage"));
const EmailAuditPage = lazy(() => import("@/pages/dash-v2/email/tools/AuditPage"));
const EmailIntegrationsPage = lazy(() => import("@/pages/dash-v2/email/tools/IntegrationsPage"));
const AutomationRulesPage = lazy(() => import("@/pages/dash-v2/AutomationRulesPage"));
const InvoicesPageV2 = lazy(() => import("@/pages/dash-v2/InvoicesPage"));
const InvoiceSettingsPage = lazy(() => import("@/pages/dash-v2/InvoiceSettingsPage"));
const CarrierVettingSwipePage = lazy(() => import("@/pages/dash-v2/verify/CarrierVettingSwipePage"));
const FocusedAlertPage = lazy(() => import("@/pages/dash-v2/FocusedAlertPage"));
const SendPackagePage = lazy(() => import("@/pages/dash-v2/SendPackagePage"));
const LoadTrustTimelinePage = lazy(() => import("@/pages/dash-v2/loads/LoadTrustTimelinePage"));

/**
 * PageFallback · Used by Suspense during lazy-route chunk loads.
 *
 * UX rule: a route swap should NEVER flash an empty/white page. With
 * `v7_startTransition` enabled, React keeps the previous route mounted
 * while the next chunk loads, so we render a transparent fallback that
 * sits over the existing page. The top RouteProgressBar provides the
 * actual visual feedback. If a chunk takes longer than ~250ms, a soft
 * dark veil + spinner fades in so the user knows work is happening.
 */
const PageFallback = () => (
  <div
    className="fixed inset-0 z-[90] flex items-center justify-center bg-background/70 backdrop-blur-sm pointer-events-none opacity-0"
    style={{ animation: "fadeInDelayed 0.3s ease-out 0.25s forwards" }}
    aria-hidden="true"
  >
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  </div>
);

const App = () => (
  <HelmetProvider>
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <GlobalErrorProvider>
    <AuthProvider>
      <CompanyModeProvider>
        <VerticalProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <CinematicTransitionProvider>
              <ScrollToTop />
              {/* Quebec / French banner. Renders nothing for non-Quebec visitors
                  and for users who have already chosen a language. Mounted
                  above RouteProgressBar so its z-index sits over the nav. */}
              <QuebecLanguageBanner />
              {/* Top progress bar on every route (public + dash) so navigation
                  always feels responsive even before the next chunk paints. */}
              <RouteProgressBar />
              {/* Mounted at App level so /dash/onboarding (and any future
                  top-level dash route that bypasses DashV2Layout) still
                  gets dash.section / dash.route tags + breadcrumbs. */}
              <DashRouteTracer />
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  {/* Public */}
                  <Route path="/" element={<Index />} />
                  {/* Homepage v2 preview · live for review at /preview-home.
                      Swap into / when approved. See src/pages/HomeV2.tsx. */}
                  <Route path="/preview-home" element={<HomeV2 />} />
                  <Route path="/docs" element={<DocsPage />} />
                  <Route path="/quickstart" element={<QuickstartPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/switching-from-carrier411" element={<SwitchingFromCarrier411Page />} />
                  <Route path="/coverage" element={<CoveragePage />} />
                  <Route path="/developers" element={<DevelopersMarketingPage />} />
                  <Route path="/agents/signup" element={<AgentSignupPage />} />
                  <Route path="/carriers" element={<CarrierOnboarding />} />
                  <Route path="/dispatchers" element={<Navigate to="/signup" replace />} />
                  {/* Non-freight onboarding routes hidden · feature-flagged */}
                  <Route path="/connect" element={<ConnectPage />} />
                  <Route path="/claim" element={<ClaimAgentPage />} />
                  <Route path="/claim/:agentId" element={<ClaimAgentPage />} />
                  <Route path="/demo" element={<DashboardDemoPage />} />
                  <Route path="/demo/scale" element={<ScaleDemoPage />} />
                  <Route path="/demo-tour" element={<DemoVideoPage />} />
                  <Route path="/integrate" element={<IntegrationGuidePage />} />
                  <Route path="/blog" element={<BlogIndex />} />
                  <Route path="/blog/:slug" element={<BlogArticle />} />
                  <Route path="/book-demo" element={<BookDemoPage />} />
                  <Route path="/verify" element={<PublicCarrierLookup />} />
                  {/* CR Registry · public registry (Wave 2.7 + 2.8).
                      Feature-flagged: when `public_registry` is false the
                      route falls through to <NotFound /> via the catch-all
                      below. Three surfaces, ordered most-specific-first so
                      the SEO route wins over the detail route on a literal
                      `/registry/cr/...` path:
                        - /registry/cr/:cr_id/:slug → SEO page (gated by
                          registry_seo_pages — additional flag so we can
                          ship indexable URLs independently of the in-app
                          detail page).
                        - /registry/:cr_id → in-app detail page.
                        - /registry → home / search (Wave 2.7). */}
                  {registryFeatures.public_registry && registryFeatures.registry_seo_pages && (
                    <Route path="/registry/cr/:cr_id/:slug" element={<RegistrySeoPage />} />
                  )}
                  {registryFeatures.public_registry && (
                    <Route path="/registry/:cr_id" element={<RegistryDetailPage />} />
                  )}
                  {registryFeatures.public_registry && (
                    <Route path="/registry" element={<RegistryHomePage />} />
                  )}
                  <Route path="/product" element={<ProductPage />} />
                  <Route path="/unsubscribe" element={<UnsubscribePage />} />

                  {/* Public driver Trust Link page */}
                  <Route path="/track/preview" element={<TrustLinkPreviewPage />} />
                  <Route path="/tl/preview" element={<TrustLinkPreviewPage />} />
                  <Route path="/track/:token" element={<Suspense fallback={<PageFallback />}><TrustLinkDriverPage /></Suspense>} />
                  <Route path="/tl/:token" element={<Suspense fallback={<PageFallback />}><TrustLinkDriverPage /></Suspense>} />
                  <Route path="/carrier/:mc" element={<Suspense fallback={<PageFallback />}><CarrierProfilePage /></Suspense>} />
                  <Route path="/broker/:mc" element={<Suspense fallback={<PageFallback />}><BrokerProfilePage /></Suspense>} />
                  <Route path="/claim-profile/:token" element={<Suspense fallback={<PageFallback />}><ClaimVerifyPage /></Suspense>} />
                  <Route path="/receipt/:receipt_token" element={<Suspense fallback={<PageFallback />}><TrustLinkReceiptPage /></Suspense>} />
                  {/* Public broker package viewer (share-token auth, no JWT). */}
                  <Route path="/pkg/:token" element={<Suspense fallback={<PageFallback />}><PublicPackageView /></Suspense>} />

                  {/* Public carrier signup + invite landing (dash-v2) */}
                  <Route path="/carriers/signup" element={<Suspense fallback={<PageFallback />}><CarrierSignupPage /></Suspense>} />
                  <Route path="/brokers/signup" element={<Suspense fallback={<PageFallback />}><BrokerSignupPage /></Suspense>} />
                  <Route path="/carriers/invite/preview" element={<CarrierInvitePreviewPage />} />
                  <Route path="/carriers/invite/:token" element={<CarrierInviteLandingPage />} />
                  {/* Programmatic SEO · /carriers/:region and /brokers/:region.
                      Static segments above (signup, invite/*) match first per RR v6
                      ranking. Unknown slugs render <Navigate to="/coverage"/>. */}
                  <Route path="/carriers/:region" element={<StateRegistryPage role="carrier" />} />
                  <Route path="/brokers/:region" element={<StateRegistryPage role="broker" />} />
                  <Route path="/refer/ca-broker/:token" element={<Suspense fallback={<PageFallback />}><CABrokerReferPage /></Suspense>} />
                  <Route path="/tl2/:token" element={<TrustLinkDriverPageV2 />} />

                  {/* Auth */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/mfa-verify" element={<MfaVerify />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />

                  {/* Legal & Security */}
                  <Route path="/security" element={<SecurityPage />} />
                  <Route path="/terms" element={<Suspense fallback={<PageFallback />}><TermsPage /></Suspense>} />
                  <Route path="/privacy" element={<Suspense fallback={<PageFallback />}><PrivacyPage /></Suspense>} />
                  <Route path="/api-terms" element={<Suspense fallback={<PageFallback />}><ApiTermsPage /></Suspense>} />
                  <Route path="/cookies" element={<Suspense fallback={<PageFallback />}><CookiePolicyPage /></Suspense>} />

                  {/* Trust · status & changelog */}
                  <Route path="/status" element={<Suspense fallback={<PageFallback />}><StatusPage /></Suspense>} />
                  <Route path="/changelog" element={<Suspense fallback={<PageFallback />}><ChangelogPage /></Suspense>} />

                  {/* Redirects */}
                  <Route path="/api-docs" element={<Navigate to="/docs" replace />} />
                  <Route path="/docs/api" element={<Navigate to="/docs" replace />} />
                  <Route path="/platform" element={<Navigate to="/dashboard/admin" replace />} />
                  <Route path="/auth/confirm" element={<Navigate to="/auth/callback" replace />} />
                  <Route path="/sign-in" element={<Navigate to="/login" replace />} />
                  <Route path="/auth" element={<Navigate to="/login" replace />} />
                  <Route path="/logout" element={<Navigate to="/login" replace />} />

                  {/* Mobile (carrier/owner-operator) · auth-gated */}
                  <Route path="/m" element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageFallback />}><MobileLayout /></Suspense>
                    </ProtectedRoute>
                  }>
                    <Route index element={<Navigate to="/m/loads" replace />} />
                    <Route path="loads" element={<Suspense fallback={<PageFallback />}><MobileLoadFeed /></Suspense>} />
                    <Route path="profile" element={<Suspense fallback={<PageFallback />}><MobileProfile /></Suspense>} />
                    <Route path="alerts" element={<Suspense fallback={<PageFallback />}><MobileAlerts /></Suspense>} />
                  </Route>

                  {/* Legacy /dashboard redirects to the new /dash. Dash v2
                      is now live; keeping /dashboard reachable so existing
                      bookmarks, login redirects, and email links all land
                      on the real dashboard. Specific legacy sub-paths map
                      to the closest dash-v2 equivalent. */}
                  <Route path="/dashboard" element={<Navigate to="/dash" replace />} />
                  <Route path="/dashboard/onboarding" element={<Navigate to="/dash/onboarding" replace />} />
                  <Route path="/dashboard/carriers" element={<Navigate to="/dash/carriers/lookup" replace />} />
                  <Route path="/dashboard/intelligence/carrier-lookup" element={<Navigate to="/dash/carriers/lookup" replace />} />
                  <Route path="/dashboard/inbox" element={<Navigate to="/dash/email/ai" replace />} />
                  <Route path="/dashboard/email-sequences" element={<Navigate to="/dash/email/follow-ups" replace />} />
                  <Route path="/dashboard/email-templates" element={<Navigate to="/dash/email/follow-ups" replace />} />
                  <Route path="/dashboard/settings/templates" element={<Navigate to="/dash/email/follow-ups" replace />} />
                  <Route path="/dashboard/trust-links" element={<Navigate to="/dash/trust-links" replace />} />
                  <Route path="/dashboard/settings" element={<Navigate to="/dash/settings" replace />} />
                  <Route path="/dashboard/settings/billing" element={<Navigate to="/dash/settings/billing" replace />} />
                  <Route path="/dashboard/settings/team" element={<Navigate to="/dash/settings/team" replace />} />
                  <Route path="/dashboard/billing" element={<Navigate to="/dash/settings/billing" replace />} />
                  <Route path="/dashboard/admin" element={<Navigate to="/dash/admin" replace />} />
                  <Route path="/dashboard/tracking" element={<Navigate to="/dash/fleet/tracking" replace />} />
                  <Route path="/dashboard/qa-testing" element={<Navigate to="/dash/admin/qa" replace />} />
                  <Route path="/dash/qa-testing" element={<Navigate to="/dash/admin/qa" replace />} />
                  {/* Error monitor / flagged carriers: both redirect to the Command
                      Center triage kanban until standalone pages are built. Sentry
                      errors now land in qa_findings and surface there. */}
                  <Route path="/dash/error-monitor" element={<Navigate to="/dash/admin/qa#triage" replace />} />
                  <Route path="/dashboard/error-monitor" element={<Navigate to="/dash/admin/qa#triage" replace />} />
                  <Route path="/dash/flagged-agents" element={<Navigate to="/dash/admin/qa#triage" replace />} />
                  <Route path="/dashboard/flagged-agents" element={<Navigate to="/dash/admin/qa#triage" replace />} />
                  {/* Intuitive but non-canonical paths a user might type or a stale
                      link might point to. Map to the real pages. */}
                  <Route path="/dash/trust-score" element={<Navigate to="/dash/carriers/my-score" replace />} />
                  <Route path="/dashboard/trust-score" element={<Navigate to="/dash/carriers/my-score" replace />} />
                  <Route path="/dash/account" element={<Navigate to="/dash/settings" replace />} />
                  <Route path="/dashboard/account" element={<Navigate to="/dash/settings" replace />} />
                  {/* Stale links from earlier nav layouts. /dash/verifications was the
                      old name for the lookup page; /dash/carriers (no /lookup suffix)
                      collided with the new partner-centric IA. */}
                  <Route path="/dash/verifications" element={<Navigate to="/dash/verify" replace />} />
                  <Route path="/dash/carriers" element={<Navigate to="/dash/partners" replace />} />
                  {/* Catch-all for any other /dashboard/* path: land on the dash home. */}
                  <Route path="/dashboard/*" element={<Navigate to="/dash" replace />} />

                  {/* Dash v2 (in progress). Mounts under /dash to keep v1 and v2
                      isolated while the rebuild is happening. Moves to /dashboard
                      once v2 is proven and the legacy routes above are deleted.
                      Routes without a dedicated page yet land on SystemStatePage
                      which shows a Phase 1 "coming soon" variant. */}
                  {/* Post-signup role picker: shown to new users before onboarding.
                      Not wrapped in ProtectedRoute — the component calls
                      getSession() directly to avoid a race with AuthProvider's
                      async onAuthStateChange handler. */}
                  <Route path="/signup/choose-role" element={<SignupRolePicker />} />
                  <Route path="/dash/onboarding" element={
                    <ProtectedRoute><DispatcherOnboardingPage /></ProtectedRoute>
                  } />
                  <Route path="/dash/onboarding/broker" element={
                    <ProtectedRoute><BrokerOnboardingPage /></ProtectedRoute>
                  } />
                  <Route path="/dash/onboarding/carrier" element={
                    <ProtectedRoute><CarrierOnboardingPage /></ProtectedRoute>
                  } />
                  <Route path="/dash" element={<ProtectedRoute><DashV2Layout /></ProtectedRoute>}>
                    <Route index element={<HomeGate />} />
                    <Route path="home" element={<AuditModeRedirect><DispatcherHomePage /></AuditModeRedirect>} />
                    <Route path="verify" element={<VerificationDashboardPage />} />
                    <Route path="verify/swipe" element={
                      <PhaseGate feature="carrier_vetting_swipe" denyMessage="Swipe vetting isn't active on your plan yet."><CarrierVettingSwipePage /></PhaseGate>
                    } />
                    <Route path="bulk-verify" element={<BulkVerifyPage />} />
                    <Route path="carriers/lookup" element={<CarrierLookupPage />} />
                    <Route path="carriers/my-score" element={<MyTrustScorePage />} />
                    <Route path="carriers/:mc" element={<CarrierDetailPage />} />
                    <Route path="brokers/lookup" element={<BrokerLookupPage />} />
                    {/* CA broker directory + detail · static path BEFORE
                        :cab_id so /dash/brokers/ca renders the list, and
                        /dash/brokers/ca/:cab_id renders the detail. Both
                        precede :mc so CAB-XX-XXXXXXX isn't swallowed by US. */}
                    <Route path="brokers/ca" element={<CABrokerListPage />} />
                    <Route path="brokers/ca/:cab_id" element={<CABrokerDetailPage />} />
                    <Route path="brokers/onboarding/ca/documents" element={<Suspense fallback={<PageFallback />}><CABrokerDocumentsPage /></Suspense>} />
                    <Route path="brokers/:mc" element={<BrokerDetailPage />} />
                    <Route path="partners" element={<MyPartnersPage />} />
                    <Route path="partners/alerts" element={<PartnerAlertsPage />} />
                    <Route path="alerts/:alertId" element={<FocusedAlertPage />} />
                    <Route path="packages/inbox" element={<PackageInboxPage />} />
                    <Route path="broker/package" element={<BrokerPackagePage />} />
                    <Route path="developers" element={<AuditModeRedirect><DevelopersPage /></AuditModeRedirect>} />
                    <Route path="developer" element={<AuditModeRedirect><DeveloperMcpPage /></AuditModeRedirect>} />
                    <Route path="inbox" element={<Navigate to="/dash/email/ai" replace />} />
                    <Route path="email" element={<Navigate to="/dash/email/ai" replace />} />
                    <Route path="email/follow-ups" element={
                      <PhaseGate feature="email_templates" denyMessage="Email templates aren't active on your plan yet."><FollowUpsPage /></PhaseGate>
                    } />
                    <Route path="email/sequences" element={
                      <PhaseGate feature="email_templates" denyMessage="Email sequences aren't active on your plan yet."><EmailSequencesPage /></PhaseGate>
                    } />
                    <Route path="sequences" element={<Navigate to="/dash/email/sequences" replace />} />
                    <Route path="email/templates" element={
                      <PhaseGate feature="email_templates" denyMessage="Email templates aren't active on your plan yet."><EmailTemplatesPage /></PhaseGate>
                    } />
                    <Route path="email/ai" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailAIPage /></PhaseGate>
                    } />
                    <Route path="email/scanner" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailScannerPage /></PhaseGate>
                    } />
                    <Route path="email/schedule" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailSchedulePage /></PhaseGate>
                    } />
                    <Route path="email/playbooks" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailPlaybooksPage /></PhaseGate>
                    } />
                    <Route path="email/audit" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailAuditPage /></PhaseGate>
                    } />
                    <Route path="email/integrations" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><EmailIntegrationsPage /></PhaseGate>
                    } />
                    <Route path="email/sent" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><SentPage /></PhaseGate>
                    } />
                    <Route path="email/kb" element={
                      <PhaseGate feature="email_ai" denyMessage="AI email is coming after Phase 1."><KnowledgeBasePage /></PhaseGate>
                    } />
                    <Route path="email/automations" element={
                      <PhaseGate feature="automation_rules" denyMessage="Automation rules aren't active on your plan yet."><AutomationRulesPage /></PhaseGate>
                    } />
                    <Route path="trust-links" element={
                      <PhaseGate feature="trust_links" denyMessage="Trust Links aren't active on your plan yet."><TrustLinksPage /></PhaseGate>
                    } />
                    <Route path="fleet/tracking" element={
                      <PhaseGate feature="fleet_tracking" denyMessage="Fleet tracking isn't active on your plan yet."><FleetTrackingPage /></PhaseGate>
                    } />
                    <Route path="loads" element={
                      <PhaseGate feature="load_board" denyMessage="Load Board isn't active on your plan yet."><DispatcherLoadBoardPage /></PhaseGate>
                    } />
                    <Route path="loads/:tenderId" element={
                      <PhaseGate feature="load_board" denyMessage="Load Board isn't active on your plan yet."><LoadTrustTimelinePage /></PhaseGate>
                    } />
                    <Route path="documents" element={
                      <PhaseGate feature="documents" denyMessage="Document management isn't active on your plan yet."><DocumentsPage /></PhaseGate>
                    } />
                    <Route path="send-package" element={<SendPackagePage />} />
                    <Route path="settings" element={<SettingsHubPageV2 />} />
                    <Route path="settings/billing" element={<BillingPageV2 />} />
                    <Route path="referrals" element={<ReferralsPage />} />
                    <Route path="settings/team" element={<TeamPageV2 />} />
                    <Route path="settings/sandbox" element={<SandboxSettingsPage />} />
                    <Route path="settings/notifications" element={<NotificationSettingsPage />} />
                    <Route path="feedback" element={<FeedbackPage />} />
                    <Route path="settings/email" element={<EmailSettingsPage />} />
                    <Route path="settings/verification" element={<VerificationSettingsPage />} />
                    <Route path="settings/locale" element={<LocaleSettingsPage />} />
                    <Route path="settings/documents" element={<DocumentSettingsPage />} />
                    <Route path="settings/deals" element={<DealSettingsPage />} />
                    <Route path="settings/trust" element={<TrustSettingsPage />} />
                    <Route path="settings/security" element={<SecuritySettingsPage />} />
                    <Route path="settings/rates" element={<RateSettingsPage />} />
                    <Route path="settings/integrations" element={<IntegrationSettingsPage />} />
                    <Route path="invoices" element={
                      <PhaseGate feature="invoices" denyMessage="Invoices aren't active on your plan yet."><InvoicesPageV2 /></PhaseGate>
                    } />
                    <Route path="invoices/settings" element={
                      <PhaseGate feature="invoices" denyMessage="Invoice settings aren't active on your plan yet."><InvoiceSettingsPage /></PhaseGate>
                    } />
                    <Route path="admin" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="analytics"><AdminAnalyticsPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/traction" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="traction"><AdminTractionPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/tenants" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="hub"><AdminHubPageV2 /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/qa" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="qa"><QACommandCenterPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/cockpit" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="cockpit"><QACockpitPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/pipelines" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="pipelines"><PipelinesPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/roadmap" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="roadmap"><AdminRoadmapPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/referrals" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="referrals"><AdminReferralsPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/tenant-controls" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="tenant-controls"><AdminTenantControlsPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/emergency-controls" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="emergency-controls"><AdminEmergencyControlsPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/embed-badge" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="embed-badge"><EmbedBadgeTesterPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/intakes" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="intakes"><IntakeMonitorPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/doc-review" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="doc-review"><DocReviewQueuePage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/ca-broker-claims" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="ca-broker-claims"><CABrokerClaimsPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/doc-bundle" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="doc-bundle"><DocBundlePage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/broker-package-ops" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="broker-package-ops"><AdminBrokerPackageOpsPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="admin/ingest-health" element={
                      <ProtectedRoute requiredRole="admin"><AdminRouteBoundary routeId="ingest-health"><AdminIngestHealthPage /></AdminRouteBoundary></ProtectedRoute>
                    } />
                    <Route path="intelligence/fraud" element={<SystemStatePage variant="phase-blocked" />} />
                    <Route path="404" element={<SystemStatePage variant="not-found" />} />
                    <Route path="403" element={<SystemStatePage variant="phase-blocked" />} />
                    <Route path="network-error" element={<SystemStatePage variant="network-error" />} />
                    <Route path="session-expired" element={<SystemStatePage variant="session-expired" />} />
                    <Route path="*" element={<SystemStatePage variant="not-found" />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <CinematicTransitionLayer />
              </CinematicTransitionProvider>
            </BrowserRouter>
          </TooltipProvider>
        </VerticalProvider>
      </CompanyModeProvider>
    </AuthProvider>
    </GlobalErrorProvider>
  </QueryClientProvider>
  </ErrorBoundary>
  </HelmetProvider>
);

export default App;
